import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Search, Plus, Edit, Trash2, Eye, MapPin, Clock, Users, Ticket, Loader2
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { toursApi } from '../../lib/api'

interface Tour {
    id: number
    type: string
    price: number
    duration: string
    location: string
    max_participants?: number
    maxParticipants?: number
    featured_image?: string
    status?: string
    title_en?: string
    title_vi?: string
    translations?: {
        en?: { title?: string; description?: string }
        vi?: { title?: string; description?: string }
    }
    created_at?: string
}

export default function TourManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [typeFilter, setTypeFilter] = useState('all')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedTour, setSelectedTour] = useState<Tour | null>(null)
    const [tours, setTours] = useState<Tour[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)

    const types = [
        { id: 'all', name: { en: 'All', vi: 'Tất cả' } },
        { id: 'island', name: { en: 'Island Tours', vi: 'Tour Đảo' } },
        { id: 'snorkeling', name: { en: 'Snorkeling', vi: 'Lặn ngắm' } },
        { id: 'fishing', name: { en: 'Fishing', vi: 'Câu cá' } },
        { id: 'sunset', name: { en: 'Sunset', vi: 'Hoàng hôn' } },
        { id: 'ticket', name: { en: 'Tickets', vi: 'Vé' } },
    ]

    const fetchTours = async () => {
        setLoading(true)
        try {
            const params: any = {}
            if (typeFilter !== 'all') params.type = typeFilter

            const response = await toursApi.adminList(params)
            if (response.data?.data) {
                setTours(response.data.data)
            }
        } catch (error) {
            console.error('Failed to fetch tours:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTours()
    }, [typeFilter])

    const getTitle = (tour: Tour) => {
        if (tour.title_en || tour.title_vi) {
            return language === 'en' ? (tour.title_en || tour.title_vi) : (tour.title_vi || tour.title_en)
        }
        if (language === 'en') return tour.translations?.en?.title || 'Untitled'
        return tour.translations?.vi?.title || tour.translations?.en?.title || 'Chưa có tiêu đề'
    }

    const handleDelete = async () => {
        if (!selectedTour) return
        setDeleting(true)
        try {
            await toursApi.delete(selectedTour.id)
            setShowDeleteModal(false)
            setSelectedTour(null)
            fetchTours()
        } catch (error) {
            console.error('Failed to delete tour:', error)
        } finally {
            setDeleting(false)
        }
    }

    const filteredTours = tours.filter((t) => {
        const title = getTitle(t)
        return title?.toLowerCase().includes(searchQuery.toLowerCase())
    })

    const stats = {
        total: tours.length,
        tours: tours.filter(t => t.type !== 'ticket').length,
        tickets: tours.filter(t => t.type === 'ticket').length,
        active: tours.filter(t => t.status === 'active').length,
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'Tour & Ticket Management' : 'Quản Lý Tour & Vé'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Manage tours and tickets' : 'Quản lý các tour và vé'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link
                        to="/admin/tours/new"
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                    >
                        <Plus className="w-5 h-5" />
                        {language === 'en' ? 'Add Tour' : 'Thêm Tour'}
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total' : 'Tổng số'}</p>
                    <p className="text-2xl font-bold mt-1">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Tours' : 'Tour'}</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600">{stats.tours}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Tickets' : 'Vé'}</p>
                    <p className="text-2xl font-bold mt-1 text-purple-600">{stats.tickets}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Active' : 'Hoạt động'}</p>
                    <p className="text-2xl font-bold mt-1 text-green-600">{stats.active}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            placeholder={language === 'en' ? 'Search tours...' : 'Tìm tour...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {types.map((type) => (
                            <button
                                key={type.id}
                                onClick={() => setTypeFilter(type.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${typeFilter === type.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {type.name[language]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tours Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTours.length === 0 ? (
                        <div className="col-span-full text-center py-12 text-gray-500">
                            {language === 'en' ? 'No tours found' : 'Không tìm thấy tour'}
                        </div>
                    ) : (
                        filteredTours.map((tour) => (
                            <div key={tour.id} className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition">
                                <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 relative">
                                    {tour.featured_image ? (
                                        <img src={tour.featured_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            {tour.type === 'ticket' ? (
                                                <Ticket className="w-12 h-12 text-purple-300" />
                                            ) : (
                                                <MapPin className="w-12 h-12 text-blue-300" />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${tour.type === 'ticket' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                            {tour.type === 'ticket' ? (language === 'en' ? 'Ticket' : 'Vé') : tour.type}
                                        </span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${tour.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                            {tour.status === 'active' ? (language === 'en' ? 'Active' : 'Hoạt động') : (language === 'en' ? 'Draft' : 'Nháp')}
                                        </span>
                                    </div>
                                    <h3 className="font-bold text-lg mb-2 line-clamp-2">{getTitle(tour)}</h3>
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {tour.duration || 'N/A'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {tour.max_participants || tour.maxParticipants || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between pt-3 border-t">
                                        <span className="text-xl font-bold text-primary-600">
                                            {formatCurrency(tour.price, 'VND')}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/tours/${tour.id}`}
                                                className="p-2 hover:bg-gray-100 rounded-lg"
                                                title="View"
                                            >
                                                <Eye className="w-4 h-4 text-gray-600" />
                                            </Link>
                                            <Link
                                                to={`/admin/tours/${tour.id}/edit`}
                                                className="p-2 hover:bg-blue-50 rounded-lg"
                                                title="Edit"
                                            >
                                                <Edit className="w-4 h-4 text-blue-600" />
                                            </Link>
                                            <button
                                                onClick={() => {
                                                    setSelectedTour(tour)
                                                    setShowDeleteModal(true)
                                                }}
                                                className="p-2 hover:bg-red-50 rounded-lg"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-2">
                            {language === 'en' ? 'Delete Tour' : 'Xóa Tour'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {language === 'en'
                                ? `Are you sure you want to delete "${getTitle(selectedTour!)}"?`
                                : `Bạn có chắc muốn xóa "${getTitle(selectedTour!)}"?`
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {language === 'en' ? 'Delete' : 'Xóa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

